import { notify } from 'utils'
import { map, omit, set, concat } from 'lodash'
import { http, queryString } from 'utils'
import { PureAbility } from '@casl/ability'
import { abilityExists } from 'utils/abilities'

// User mutation imports.
import { SET_PROPERTY, SET_AUTHORIZED, SET_USERS, SET_USER, SET_TIMESHEET, ADD_USERS, SET_USERS_PAGINATION, SET_TIMER } from 'vx/mutations'

const initialUserState = () => {
  return {
    id: null,
    first_name: null,
    last_name: null,
    email: null
  }
}

// User state.
const state = {
  authorized: false,
  user: initialUserState(),
  timesheet: [],
  users: [],
  timer: null
}

// User state getters.
const getters = {
  /**
   * Authorized user state as a decorated object.
   */
  authorized: state => state.authorized,

  /**
   * User state as a decorated object.
   */
  user: state => state.user,

  /**
   * User options for drop down.
   */
  userOptions: state => map(state.users, user => ({ id: user.id, name: user.name })),

  /**
   * Default authorized user object for vue-multiselect use
   */
  authorizedSelect: state => {
    return {id: state.authorized.id, name: state.authorized.name}
  },

  /**
   * Timer for this user.
   */
  timer: state => state.timer,

  ability () {
    return new PureAbility([])
  },
  can: (state, getters) => (ability) => {
    if (abilityExists(ability)) {
      return getters.ability.can(ability)
    }

    throw new Error(`Undefined ability "${ability}", check your spelling!`)
  },
  cannot: (state, getters) => (ability) => !getters.can(ability),
  isA: (state, getters) => (role) => state.authorized.role && (state.authorized.role.name === role),
  isNotA: (state, getters) => (role) => state.authorized.role && (state.authorized.role.name !== role)
}

// User actions.
const actions = {

  /**
   * Attempt to authorize a user by fetching the logged in user details.
   *
   * @return Promise
   */
  authorize ({ commit, getters }) {
    return http.get('users/me')
      .then((response) => {

        if (response.data.data.activeTimer) {
          commit(SET_TIMER, response.data.data.activeTimer)
          delete(response.data.data.activeTimer)
        }

        commit(SET_AUTHORIZED, response.data.data)

        return getters.authorized
      })
  },

  /**
   * Log a user in and set the authorized user.
   *
   * @return Promise
   */
  login ({ commit }, { email, password, token }) {
    return http.post('login', { email, password, token })
      .then((response) => {

        if (response.data.user.timer) {
          commit(SET_TIMER, response.data.user.activeTimer)
          delete(response.data.user.activeTimer)
        }

        commit(SET_AUTHORIZED, response.data.user)

        return response
      })
  },

  googleQRCode() {
    return http.get('users/google2faurl')
      .then((response) => {
        return response
      })
  },

  getUser({ commit }, id) {
    return http.get(`users/${id}`).then(response => {
      commit(SET_USER, response.data.data)

      return response
    })
  },

  getUserTimesheet({ commit }, { id, payload }) {
    return http.get(`users/${id}/timesheet?${queryString(payload)}`).then(response => {
      commit(SET_TIMESHEET, response.data.data)

      return response
    })
  },

  /**
   * Get a list of users.
   */
  getUsers ({ commit }, payload) {
    return http.get(`users?${queryString(payload)}`).then(response => {
      commit(SET_USERS, response.data.data)
      commit(SET_USERS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * Get the users for the next page.
   */
  getUsersForNextPage ({ commit, state }, payload) {
    if (state.pagination.current_page === state.pagination.last_page) {
      return Promise.reject('There are no more users to load.')
    }

    let page = state.pagination.current_page + 1

    return http.get(`users?${queryString(payload)}&page=${page}`).then(response => {

      commit(ADD_USERS, response.data.data)
      commit(SET_USERS_PAGINATION, omit(response.data, 'data'))

      return response
    })
  },

  /**
   * delete a user by sending a delete request.
   */
  deleteUser ({ commit }, id) {
    return http.delete(`users/${id}`).then(response => {
      state.users.forEach((user, index) => {
          if(user.id === response.entity.id) {
              state.users.splice(index, 1)
          }
      });
    })
  },

  /**
   * Start a timer for this user.
   *
   * @return Promise
   */
  startTimer ({ commit },  payload) {
    return http.post('timers', payload)
      .then((response) => {
        commit(SET_TIMER, response.data)

        return response
      }).catch(function (error) {

        if (error.response && error.response.status && error.response.status === 422) {
            let message = "Form validation failed:<br>"

            if (error.response.data && error.response.data.errors) {
                Object.keys(error.response.data.errors).forEach((e) => {
                    message = message + `<br><strong>${_.upperFirst(e)}</strong>: ${error.response.data.errors[e]}`
                });
            }

            notify.error(message)
        }

        else if (error.response) {
            notify.error("API Error: " + error.response.status + " <br> " + error.response.statusText)
        }
    })
  },

   /**
   * Pause a timer for this user.
   *
   * @return Promise
   */
  pauseTimer ({ commit },  {id, payload}) {
    return http.put(`timers/${id}`, payload)
      .then((response) => {
        commit(SET_TIMER, response.data)

        return response
      }).catch(function (error) {

        if (error.response && error.response.status && error.response.status === 422) {
            let message = "Form validation failed:<br>"

            if (error.response.data && error.response.data.errors) {
                Object.keys(error.response.data.errors).forEach((e) => {
                    message = message + `<br><strong>${_.upperFirst(e)}</strong>: ${error.response.data.errors[e]}`
                });
            }

            notify.error(message)
        }

        else if (error.response) {
            notify.error("API Error: " + error.response.status + " <br> " + error.response.statusText)
        }

        else {
          notify.error(error)
        }
    })
  },

  /**
   * Pause a timer for this user.
   *
   * @return Promise
   */
  resumeTimer ({ commit },  {id, payload}) {
    return http.put(`timers/${id}`, payload)
      .then((response) => {
        commit(SET_TIMER, response.data)

        return response
      }).catch(function (error) {

        if (error.response && error.response.status && error.response.status === 422) {
            let message = "Form validation failed:<br>"

            if (error.response.data && error.response.data.errors) {
                Object.keys(error.response.data.errors).forEach((e) => {
                    message = message + `<br><strong>${_.upperFirst(e)}</strong>: ${error.response.data.errors[e]}`
                });
            }

            notify.error(message)
        }

        else if (error.response) {
            notify.error("API Error: " + error.response.status + " <br> " + error.response.statusText)
        }
    })
  },

  /**
   * Log a timer for this user.
   *
   * @return Promise
   */
  logTimer ({ commit },  {id, payload}) {
    return http.put(`timers/${id}/log`, payload)
      .then((response) => {
        if (typeof response.data === 'object' && response.data !== null) {
          commit(SET_TIMER, null)
          return response.data
        }
      }).catch(function (error) {

        if (error.response && error.response.status && error.response.status === 422) {
            let message = "Form validation failed:<br>"

            if (error.response.data && error.response.data.errors) {
                Object.keys(error.response.data.errors).forEach((e) => {
                    message = message + `<br><strong>${_.upperFirst(e)}</strong>: ${error.response.data.errors[e]}`
                });
            }

            notify.error(message)
        }

        else if (error.response) {
            notify.error("API Error: " + error.response.status + " <br> " + error.response.statusText)
        }
    })
  },

  /**
   * Log a timer for this user.
   *
   * @return Promise
   */
  deleteTimer ({ commit },  {id, payload}) {
    return http.delete(`timers/${id}`, payload)
      .then((response) => {
        if (typeof response.data === 'object' && response.data !== null) {
          commit(SET_TIMER, response.data)
        } else {
          commit(SET_TIMER, null)
        }

        return response
      }).catch(function (error) {

        if (error.response && error.response.status && error.response.status === 422) {
            let message = "Form validation failed:<br>"

            if (error.response.data && error.response.data.errors) {
                Object.keys(error.response.data.errors).forEach((e) => {
                    message = message + `<br><strong>${_.upperFirst(e)}</strong>: ${error.response.data.errors[e]}`
                });
            }

            notify.error(message)
        }

        else if (error.response) {
            notify.error("API Error: " + error.response.status + " <br> " + error.response.statusText)
        }
    })
  },

  setProperty ({ commit }, payload) {
    commit(SET_PROPERTY, payload)
  },
}

// User state mutations.
const mutations = {
  /**
   * Set the authorized user object.
   *
   * @return void
   */
  [SET_AUTHORIZED] (state, user) {
    state.authorized = user
  },

  /**
  * Set the users state.
  */
  [SET_USERS] (state, users) {
    state.users = users
  },

  /**
  * Set the users state.
  */
  [SET_USER] (state, user) {
    state.user = user
  },

  [SET_TIMESHEET] (state, timesheet) {
    state.timesheet = timesheet
  },

  /**
   * Add more users to the users state.
   */
  [ADD_USERS] (state, users) {
    state.users = concat(state.users, users)
  },

  /**
   * Set the users pagination.
   */
  [SET_USERS_PAGINATION] (state, pagination) {
    state.pagination = pagination
  },
  /**
   * Set the timer againts the authorized user.
   *
   * @return void
   */
  [SET_TIMER] (state, timer) {
    state.timer = timer
  },

  [SET_PROPERTY] (state, { property, value, index }) {
    set(state.user, property, value)
  },
}

// Export our state, getters, actions, mutations, and modules.
export default {
  namespaced: true, state, getters, actions, mutations
}
